import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Stack, TextField, Typography,
} from '@mui/material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

export default function CrmApiKeys() {
  const [keys, setKeys] = useState([]);
  const [stations, setStations] = useState([]);
  const [name, setName] = useState('RadioCRM');
  const [stationIds, setStationIds] = useState([]);
  const [err, setErr] = useState('');
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  const linked = stations.filter((s) => s.crm_link_enabled);
  const internal = stations.filter((s) => !s.crm_link_enabled);

  const reload = () => axios.get(`${API_URL}/api/integrations/crm/keys`, { withCredentials: true })
    .then((res) => setKeys(res.data || []))
    .catch((e) => setErr(e.response?.data?.message || e.message));

  useEffect(() => {
    reload();
    axios.get(`${API_URL}/api/stations`, { withCredentials: true })
      .then((res) => setStations(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  const toggleStation = (id) => {
    setStationIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const create = async () => {
    if (!stationIds.length) {
      setErr('Pick at least one RadioCRM-linked station. Internal stations cannot be added to a key.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const res = await axios.post(`${API_URL}/api/integrations/crm/keys`, {
        name,
        station_ids: stationIds,
      }, { withCredentials: true });
      setCreated(res.data);
      setName('RadioCRM');
      setStationIds([]);
      reload();
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this key? RadioCRM will stop pushing campaigns until a new key is pasted.')) return;
    try {
      await axios.post(`${API_URL}/api/integrations/crm/keys/${id}/revoke`, {}, { withCredentials: true });
      reload();
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    }
  };

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      /* ignore */
    }
  };

  return (
    <Card sx={{ borderRadius: 2, mt: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ color: 'primary.main', mb: 1 }}>RadioCRM API</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Superadmin only. Linking is per station: turn RadioCRM on for commercial stations under Stations,
          then create a key that lists only those stations. Internal or studio stations stay off and never
          appear to RadioCRM. The key is HMAC-SHA256 — it is not a user login and does not use MFA. The
          secret is shown once.
        </Typography>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
        {!linked.length && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No stations allow RadioCRM yet. Open Stations and enable “Allow RadioCRM” on each commercial
            station. Leave internal stations off.
          </Alert>
        )}
        <Stack spacing={2} sx={{ mb: 3 }}>
          <TextField label="Key name" value={name} onChange={(e) => setName(e.target.value)} size="small" />
          <Box>
            <Typography variant="caption" color="text.secondary">Stations this key may touch (required)</Typography>
            <Stack>
              {linked.map((s) => (
                <FormControlLabel
                  key={s.id}
                  control={<Checkbox checked={stationIds.includes(s.id)} onChange={() => toggleStation(s.id)} />}
                  label={`${s.name}${s.call_sign ? ` (${s.call_sign})` : ''} · id ${s.id}`}
                />
              ))}
              {internal.map((s) => (
                <FormControlLabel
                  key={s.id}
                  disabled
                  control={<Checkbox checked={false} />}
                  label={`${s.name}${s.call_sign ? ` (${s.call_sign})` : ''} · internal — enable RadioCRM on Stations first`}
                />
              ))}
            </Stack>
          </Box>
          <Button
            variant="contained"
            onClick={create}
            disabled={busy || !stationIds.length}
            sx={{ alignSelf: 'flex-start' }}
          >
            Create API key
          </Button>
        </Stack>
        {keys.map((k) => (
          <Box key={k.id} sx={{ p: 1.5, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography fontWeight={600}>{k.name} · {k.key_prefix}</Typography>
            <Typography variant="body2" color="text.secondary">
              {k.active ? 'Active' : 'Revoked'}
              {k.last_used_at ? ` · last used ${new Date(k.last_used_at).toLocaleString()}` : ' · never used'}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {(k.stations || []).map((s) => (
                <Chip
                  key={s.id}
                  size="small"
                  label={s.name || `Station ${s.id}`}
                  color={s.crm_link_enabled ? 'primary' : 'default'}
                />
              ))}
              {!(k.stations || []).length && (
                <Chip size="small" label="No stations — recreate this key" color="warning" />
              )}
            </Stack>
            {k.active && (
              <Button size="small" color="error" onClick={() => revoke(k.id)} sx={{ mt: 1 }}>Revoke</Button>
            )}
          </Box>
        ))}
      </CardContent>
      <Dialog open={Boolean(created?.plaintext)} onClose={() => setCreated(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy this key now</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {created?.warning || 'This is the only time Radio Suite will show the secret.'}
          </Alert>
          <TextField
            fullWidth
            multiline
            value={created?.plaintext || ''}
            InputProps={{ readOnly: true, sx: { fontFamily: 'JetBrains Mono, monospace', fontSize: 13 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => copy(created?.plaintext || '')}>Copy</Button>
          <Button variant="contained" onClick={() => setCreated(null)}>I have saved it</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
